/**
 * 🧭 CONTEXTO ORÇAMENTÁRIO (coligada / filial / ano)
 *
 * O contexto vive em três cópias no localStorage, e por muito tempo elas podiam
 * divergir:
 *
 *   - `selectedFilial` ("1-10") e `selectedYear` — o que o cabeçalho da Home
 *     mostra ("2026 | C1F10");
 *   - `PCODCOLIGADA` / `PCODFILIAL` — o que TODAS as telas de lançamento leem
 *     para montar o WHERE;
 *   - `CODCOLIGADA` / `CODFILIAL` — cópia antiga, mantida por compatibilidade.
 *
 * Quando só a primeira era gravada, o cabeçalho aparecia preenchido e a consulta
 * ia ao banco com `CODCOLIGADA = 'null'` — a string, porque `getItem` devolve
 * `null` e o template literal o interpola. Não dava erro: voltava zero linha,
 * com cara de "não tem dado".
 *
 * Este módulo é o único lugar que grava contexto. Grava as três cópias de uma
 * vez, ou nenhuma.
 */

const CHAVES = {
  filial: "selectedFilial",
  ano: "selectedYear",
  pColigada: "PCODCOLIGADA",
  pFilial: "PCODFILIAL",
  coligada: "CODCOLIGADA",
  codFilial: "CODFILIAL",
  nomeFilial: "NomeFilial",
};

// "null" e "undefined" entram na lista porque já houve gravação de valor
// interpolado; uma vez no localStorage, viram string e passariam por um
// `if (valor)` desavisado.
const vazio = (valor) =>
  valor === null ||
  valor === undefined ||
  valor === "" ||
  valor === "null" ||
  valor === "undefined";

/** Contexto atual, já normalizado — campo ausente vem como `null`. */
export function lerContexto() {
  const coligada = localStorage.getItem(CHAVES.pColigada);
  const filial = localStorage.getItem(CHAVES.pFilial);
  const ano = localStorage.getItem(CHAVES.ano);

  return {
    coligada: vazio(coligada) ? null : String(coligada),
    filial: vazio(filial) ? null : String(filial),
    ano: vazio(ano) ? null : String(ano),
  };
}

export function contextoCompleto() {
  const { coligada, filial, ano } = lerContexto();
  return Boolean(coligada && filial && ano);
}

/**
 * Grava as três cópias juntas. Devolve `false` sem escrever nada se coligada ou
 * filial vierem vazias — meio contexto é pior que nenhum, porque passa a
 * impressão de que está tudo certo.
 */
export function gravarContexto({ coligada, filial, nomeFilial, ano }) {
  if (vazio(coligada) || vazio(filial)) return false;

  const c = String(coligada);
  const f = String(filial);

  localStorage.setItem(CHAVES.filial, `${c}-${f}`);
  localStorage.setItem(CHAVES.pColigada, c);
  localStorage.setItem(CHAVES.pFilial, f);
  localStorage.setItem(CHAVES.coligada, c);
  localStorage.setItem(CHAVES.codFilial, f);

  if (!vazio(nomeFilial)) localStorage.setItem(CHAVES.nomeFilial, nomeFilial);
  if (!vazio(ano)) localStorage.setItem(CHAVES.ano, String(ano));

  return true;
}

/**
 * Conserto sem rede. `selectedFilial` é a cópia que sempre foi gravada (é dela
 * que sai o "C1F10" do cabeçalho), então quando as chaves P faltam dá para
 * derivá-las dali mesmo. Cobre os casos em que o usuário aplicou o contexto pelo
 * modal antes da lista de filiais carregar.
 *
 * Devolve `true` se o contexto está completo ao final.
 */
export function repararContexto() {
  const atual = lerContexto();

  if (!atual.ano) {
    // Sem ano a consulta também sairia com ANO = 'null'. A Home já usa o ano
    // corrente como padrão quando a API falha; mesma regra aqui.
    localStorage.setItem(CHAVES.ano, String(new Date().getFullYear()));
  }

  if (atual.coligada && atual.filial) return true;

  const chave = localStorage.getItem(CHAVES.filial);
  if (vazio(chave)) return false;

  const [coligada, filial] = String(chave).split("-");
  if (vazio(coligada) || vazio(filial)) return false;

  gravarContexto({ coligada, filial });
  return contextoCompleto();
}
